import { createContext, useContext, useEffect, useState, ReactNode } from "react"
import { useRouter, usePathname } from "@/lib/router-helpers"
import {jwtDecode} from "jwt-decode"; 
import Cookies from "js-cookie"
import { useServerTime } from "@/utils/server-time" 

export interface User {
  id?: string
  mobile: string
  firstname: string
  lastname: string
  email?: string
  active?: boolean
  created_at?: Date
  updated_at?: Date

}
//Testtt
interface JwtCustomPayload {
  data: User
  exp: number
  iat: number
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  login: ( token: string) => void
  logout: () => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children}: { children: ReactNode}) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()
  const pathname = usePathname()
  const { serverTimeSeconds, serverTimeMs } = useServerTime()

  useEffect(() => {
    // Load user and token from localStorage on mount
    if (typeof window === 'undefined') {
      setIsLoading(false)
      return
    }
    
    try {
      const storedUser = localStorage.getItem("qetero.user")
      const storedToken = Cookies.get("qetero.token")

      if (storedToken) {
        try {
          const decoded = jwtDecode<JwtCustomPayload>(storedToken)
          const hasServerTime = serverTimeSeconds > 0

          if (hasServerTime && decoded.exp < serverTimeSeconds) {
            localStorage.removeItem("qetero.user")
            Cookies.remove("qetero.token")
            localStorage.removeItem("qetero.currentUserPhone")
            setUser(null)
            setToken(null)
            // Redirect to login so user always sees logout (not just cleared state on same page)
            if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/')) {
              const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
              window.location.replace(`/auth/verify?returnUrl=${returnUrl}`)
            }
          } else {
            // Validate that the stored user has the minimum identity info.
            // Lastname can legitimately be empty for some accounts.
            if (storedUser) {
              try {
                const parsedUser: User = JSON.parse(storedUser)
                if (!parsedUser.mobile || !parsedUser.firstname) {
                  console.warn("[Auth] Invalid user in storage (missing mobile/firstname). Clearing session.")
                  localStorage.removeItem("qetero.user")
                  Cookies.remove("qetero.token")
                  localStorage.removeItem("qetero.currentUserPhone")
                  setUser(null)
                  setToken(null)
                } else {
                  setToken(storedToken)
                  setUser(parsedUser)
                }
              } catch (e) {
                console.error("[Auth] Failed to parse stored user. Clearing session.", e)
                localStorage.removeItem("qetero.user")
                Cookies.remove("qetero.token")
                localStorage.removeItem("qetero.currentUserPhone")
                setUser(null)
                setToken(null)
              }
            } else {
              // No stored user but we have a token → also treat as invalid
              console.warn("[Auth] Token found without user data. Clearing session.")
              localStorage.removeItem("qetero.user")
              Cookies.remove("qetero.token")
              localStorage.removeItem("qetero.currentUserPhone")
              setUser(null)
              setToken(null)
            }
          }
        } catch (error) {
          console.error("Invalid token:", error)
          localStorage.removeItem("qetero.user")
          Cookies.remove("qetero.token")
          localStorage.removeItem("qetero.currentUserPhone")
          localStorage.removeItem("qetero.token")
          localStorage.removeItem("token")
          setUser(null)
          setToken(null)
        }
      }
    } catch {
      setUser(null)
      setToken(null)
    } finally {
      setIsLoading(false)
    }
  }, [serverTimeSeconds])

  // Protect routes that require authentication
  useEffect(() => {
    if (isLoading) return

    const protectedRoutes = ["/booking/service", "/booking/schedule", "/booking/history", "/profile"]
    const isProtectedRoute = protectedRoutes.some((route) => pathname?.includes(route) || pathname?.includes(route + "/"))
    
    // Check both user and token to ensure proper authentication
    // Also check cookie directly to handle race conditions during login/navigation
    const cookieToken = Cookies.get("qetero.token")
    
    let isTokenExpired = false
    if (cookieToken) {
      try {
        const decoded = jwtDecode<JwtCustomPayload>(cookieToken)
        if (serverTimeSeconds > 0 && decoded.exp < serverTimeSeconds) {
          isTokenExpired = true
        }
      } catch {
        isTokenExpired = true
      }
    }

    if (isProtectedRoute && ((!token && !cookieToken) || isTokenExpired)) {
      if (isTokenExpired) {
        localStorage.removeItem("qetero.user")
        Cookies.remove("qetero.token")
        localStorage.removeItem("qetero.currentUserPhone")
        localStorage.removeItem("qetero.token")
        localStorage.removeItem("token")
        setUser(null)
        setToken(null)
      }

      if (typeof window !== 'undefined') {
        const returnUrl = encodeURIComponent(pathname + window.location.search)
        window.location.href = `/auth/verify?returnUrl=${returnUrl}`
      }
    }
  }, [user, token, isLoading, pathname, router, serverTimeSeconds])

  const login = (tokenValue: string) => {
    if (typeof window === 'undefined') return
    
    const decodedToken = jwtDecode<JwtCustomPayload>(tokenValue);;
    const userData : User = decodedToken?.data

    // If we received a token without minimum identity data,
    // immediately clear
    // any auth state and redirect to login instead of keeping a
    // half-broken session that shows "undefined undefined".
    // Note: lastname can be empty for some users, so we do not require it.
    if (!userData?.mobile || !userData?.firstname) {
      console.error("[Auth] Received token with missing mobile/firstname. Forcing logout.")
      localStorage.removeItem("qetero.user")
      Cookies.remove("qetero.token")
      localStorage.removeItem("qetero.currentUserPhone")
      localStorage.removeItem("qetero.token")
      localStorage.removeItem("token")
      setUser(null)
      setToken(null)

      const timestamp = serverTimeMs > 0 ? serverTimeMs : 0
      const targetUrl = `/auth/verify/?_=${timestamp}`
      window.location.replace(targetUrl)
      return
    }

    localStorage.setItem("qetero.user", JSON.stringify(userData))
    Cookies.set("qetero.token", tokenValue, { expires: 7, path: '/' }) // Expires in 7 days
    localStorage.setItem("qetero.currentUserPhone", userData.mobile)
    // Store token in both a namespaced and legacy key for compatibility
    localStorage.setItem("qetero.token", tokenValue)
    localStorage.setItem("token", tokenValue)
    setUser(userData)
    setToken(tokenValue)
  }

  const logout = () => {
    if (typeof window === 'undefined') return
    
    localStorage.removeItem("qetero.user")
    Cookies.remove("qetero.token")
    localStorage.removeItem("qetero.currentUserPhone")
    localStorage.removeItem("qetero.token")
    localStorage.removeItem("token")
    setUser(null)
    setToken(null)
    
    // Force a full page reload with cache-busting for Safari
    // Safari aggressively caches RSC payloads, causing raw JSON to display
    const timestamp = serverTimeMs > 0 ? serverTimeMs : 0
    const targetUrl = `/auth/verify/?_=${timestamp}`
    
    // Use location.replace to avoid back-button issues and force fresh load
    window.location.replace(targetUrl)
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, isAuthenticated: !!token}}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}